import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link as MuiLink,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Accordion from '../../../components/Accordion/Accordion';
import Section from '../../../components/Main/Section/Section';
import ActionBar from '../../../components/ActionBar/ActionBar';
import ContentContainer from '../../../components/Containers/ContentContainer';
import LeftContainer from '../../../components/Containers/LeftContainer';
import Confirm from '../../../components/Confirm/Confirm';
import GroupWizard from '../../../components/GroupWizard/GroupWizard';
import GroupService from '../../../services/GroupService';
import ReviewMonitoringService from '../../../services/ReviewMonitoringService';
import { IGroup } from '../../../shared/interfaces/IGroup';
import { IMonitoring } from '../../../shared/interfaces/IMonitoring';
import { CurrentViewContext } from '../../../providers/CurrentViewProvider';
import MonitoringNoContent from '../../../assets/images/monitoring_no_content.png';

const Groups = () => {
  const { setCurrentView } = useContext(CurrentViewContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [monitoringEntries, setMonitoringEntries] = useState<IMonitoring[]>([]);

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<IGroup | null>(null);

  useEffect(() => {
    setCurrentView(t('nav_groups'));
    document.title = `${t('app_branding')} ${t('nav_groups')}`;
  }, [setCurrentView, t]);

  const loadData = useCallback(async (signal: AbortSignal) => {
    setLoading(true);
    try {
      const [groupsData, monitoringData] = await Promise.all([
        GroupService.getGroups(),
        ReviewMonitoringService.getMonitoring(true, 'both', setLoading, signal),
      ]);
      setGroups(groupsData);
      if (Array.isArray(monitoringData)) {
        setMonitoringEntries(monitoringData as IMonitoring[]);
      }
    } catch (error) {
      console.error('Failed to load groups data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await GroupService.deleteGroup(deleteTargetId);
      setDeleteTargetId(null);
      await loadData(new AbortController().signal);
    } catch (error) {
      console.error('Failed to delete group:', error);
    }
  };

  const handleEditSubmit = async (data: {
    name: string;
    color: string;
    monitoringEntryIds: string[];
  }) => {
    if (!editTarget) return;
    try {
      await GroupService.updateGroup(editTarget.id, data);
      setEditTarget(null);
      await loadData(new AbortController().signal);
    } catch (error) {
      console.error('Failed to update group:', error);
    }
  };

  if (loading && groups.length === 0) {
    return (
      <Section>
        <LinearProgress />
      </Section>
    );
  }

  function getFaqLink() {
    return process.env.REACT_APP_FAQ_LINK ?? '';
  }

  if (groups.length === 0) {
    return (
      <Section>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            borderRadius: `${theme.shape.borderRadius}px`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <img
            width={300}
            src={MonitoringNoContent}
            alt="Groups no records"
          />
          <Typography sx={{ mt: theme.spacing(4) }}>{t('no_records')}</Typography>
          <Alert
            severity="warning"
            sx={{ maxWidth: '650px', my: theme.spacing(6) }}
          >
            <AlertTitle sx={{ fontWeight: '700' }}>{t('groups_alert_title')}</AlertTitle>
            {t('groups_alert_text')}
            <Link to={getFaqLink()}>FAQ´s.</Link>
          </Alert>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('guide')}
            sx={{ mt: theme.spacing(2) }}
          >
            {t('add_group')}
          </Button>
        </Box>
      </Section>
    );
  }

  return (
    <Section>
      {loading && <LinearProgress />}
      <ContentContainer>
        <LeftContainer>
          <ActionBar
            children={
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => navigate('guide')}
                >
                  {t('add_group')}
                </Button>
              </Box>
            }
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(1),
              mt: theme.spacing(1),
            }}
          >
            {groups.map((group) => (
              <Accordion
                key={group.id}
                expanded={false}
                onExpandRequest={() => undefined}
                selfManageExpanded={true}
                sx={{ borderRadius: `${theme.shape.borderRadius}px`, overflow: 'hidden' }}
                summarySx={{
                  borderLeft: `6px solid ${group.color}`,
                  '& .MuiAccordionSummary-content': {
                    alignItems: 'center',
                    gap: theme.spacing(1.5),
                  },
                }}
                summary={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      width: '100%',
                      gap: theme.spacing(1.5),
                    }}
                  >
                    {/* Bold tag name in group color */}
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, color: group.color, flexShrink: 0 }}
                    >
                      {group.name}
                    </Typography>

                    {/* Color pill */}
                    <Box
                      sx={{
                        width: '2em',
                        height: '1em',
                        borderRadius: '4px',
                        backgroundColor: group.color,
                        flexShrink: 0,
                      }}
                    />

                    {/* Entry count */}
                    <Typography
                      variant="body2"
                      sx={{ flexGrow: 1 }}
                    >
                      {group.monitoringEntries.length === 1
                        ? `${group.monitoringEntries.length} ${t('monitoring_entry_singular')}`
                        : `${group.monitoringEntries.length} ${t('monitoring_entry_plural')}`}
                    </Typography>

                    {/* Edit & Delete buttons — stop propagation so accordion doesn't toggle */}
                    <Box
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: theme.spacing(0.5),
                        ml: 'auto',
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => setEditTarget(group)}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTargetId(group.id)}
                        sx={{ color: theme.palette.error.main }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                }
                details={
                  group.monitoringEntries.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {t('no_records')}
                    </Typography>
                  ) : (
                    <Box
                      component="ul"
                      sx={{ m: 0, pl: theme.spacing(2) }}
                    >
                      {group.monitoringEntries.map((entry) => (
                        <li key={entry.id}>
                          <MuiLink
                            component={Link}
                            to="/monitoring/platforms"
                            underline="hover"
                          >
                            <Typography variant="body2">
                              {entry.label} — {entry.company}
                            </Typography>
                          </MuiLink>
                        </li>
                      ))}
                    </Box>
                  )
                }
              />
            ))}
          </Box>
        </LeftContainer>
      </ContentContainer>

      {/* Delete confirmation */}
      <Confirm
        text={t('delete_record_confirm')}
        open={deleteTargetId !== null}
        onCancel={() => setDeleteTargetId(null)}
        onOk={handleDelete}
      />

      {/* Edit dialog */}
      <Dialog
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('nav_groups')}</DialogTitle>
        <DialogContent>
          {editTarget && (
            <GroupWizard
              monitoringEntries={monitoringEntries}
              initialData={editTarget}
              onSubmit={handleEditSubmit}
              onCancel={() => setEditTarget(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </Section>
  );
};

export default Groups;
